"use client";

import { useEffect, useMemo, useState } from "react";
import { Bug, CircleX, ImagePlus, Loader2, ChevronLeft } from "lucide-react";
import { useToast } from "@/components/ui/feedback/ToastProvider";
import { api } from "@/trpc/react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/shadcn/dialog";

type BugReportProps = {
  label?: string;
  className?: string;
  source?: string;
};

type BugReportDialogProps = {
  open: boolean;
  onClose: () => void;
  onBack?: () => void;
  source?: string;
  title?: string;
  enableAttachments?: boolean;
};

export function BugReportDialog({
  open,
  onClose,
  onBack,
  source = "dashboard",
  title = "Report a bug",
  enableAttachments = false,
}: BugReportDialogProps) {
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const { showToast } = useToast();

  const isValid = useMemo(() => message.trim().length >= 5, [message]);

  const supportCreate = api.support.create.useMutation({
    onSuccess: (res) => {
      const idText = res?.id ? ` (ticket #${res.id})` : "";
      showToast(`Thanks! We logged your report${idText}.`);
      // Cleanup blob URLs before closing
      previews.forEach((url) => URL.revokeObjectURL(url));
      onClose();
    },
    onError: (e) => {
      const msg = e?.message ?? "Failed to send bug report.";
      setError(msg);
      showToast(msg, "error");
    },
  });

  // Cleanup blob URLs on unmount or when dialog closes
  useEffect(() => {
    if (!open) {
      setError(null);
      setSubmitting(false);
      setMessage("");
      setFiles([]);
      setPreviews((prev) => {
        prev.forEach((url) => URL.revokeObjectURL(url));
        return [];
      });
    }
  }, [open]);

  // Cleanup all blob URLs on component unmount
  useEffect(() => {
    return () => {
      previews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [previews]);

  function handlePickFiles(event: React.ChangeEvent<HTMLInputElement>) {
    if (!enableAttachments) return;
    const picked = Array.from(event.target.files ?? []);
    if (!picked.length) return;

    processFiles(picked);
  }

  function processFiles(picked: File[]) {
    if (!enableAttachments || !picked.length) return;

    // Validate total count first
    if (files.length + picked.length > 5) {
      setError("You can attach up to 5 images total.");
      return;
    }

    // Validate each file and collect valid ones
    const validFiles: File[] = [];
    const errors: string[] = [];

    for (const file of picked) {
      if (!file.type.startsWith("image/")) {
        errors.push(`"${file.name}" is not an image.`);
        continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        errors.push(`"${file.name}" is too large (max 5 MB).`);
        continue;
      }
      validFiles.push(file);
    }

    // Add valid files
    if (validFiles.length > 0) {
      const urls = validFiles.map((file) => URL.createObjectURL(file));
      setFiles((prev) => [...prev, ...validFiles]);
      setPreviews((prev) => [...prev, ...urls]);
    }

    // Show error for invalid files, but don't block valid ones
    if (errors.length > 0) {
      setError(errors.join(" "));
    } else {
      setError(null);
    }
  }

  function removeFile(index: number) {
    if (!enableAttachments) return;
    URL.revokeObjectURL(previews[index]);
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => prev.filter((_, i) => i !== index));
  }

  function handleDrop(event: React.DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    if (!enableAttachments) return;

    const droppedFiles = Array.from(event.dataTransfer.files);
    processFiles(droppedFiles);
  }

  function handleDragOver(event: React.DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    if (enableAttachments) {
      setIsDragging(true);
    }
  }

  function handleDragLeave(event: React.DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  }

  function handlePaste(event: React.ClipboardEvent) {
    if (!enableAttachments) return;

    const items = Array.from(event.clipboardData.items);
    const imageFiles: File[] = [];

    for (const item of items) {
      if (item.type.startsWith("image/")) {
        const file = item.getAsFile();
        if (file) imageFiles.push(file);
      }
    }

    if (imageFiles.length > 0) {
      event.preventDefault();
      processFiles(imageFiles);
    }
  }

  async function submitBug() {
    if (!isValid) return;
    setError(null);
    setSubmitting(true);
    try {
      // Upload images to blob storage first
      const imageUrls: string[] = [];

      if (enableAttachments && files.length > 0) {
        for (const file of files) {
          try {
            const formData = new FormData();
            formData.append("file", file);

            const response = await fetch("/api/support/upload-image", {
              method: "POST",
              body: formData,
            });

            if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.error || `Failed to upload ${file.name}`);
            }

            const result = await response.json();
            imageUrls.push(result.imageUrl);
          } catch (uploadError) {
            // If one image fails, show error and abort
            const errorMsg =
              uploadError instanceof Error ? uploadError.message : `Failed to upload ${file.name}`;
            setError(errorMsg);
            setSubmitting(false);
            return;
          }
        }
      }

      await supportCreate.mutateAsync({
        message: message.trim(),
        imageUrls,
        source,
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="mx-4 w-full max-w-md overflow-hidden rounded-2xl border border-[hsl(var(--brand-violet-300))]/50 bg-background shadow-xl sm:mx-0 sm:max-w-4xl p-0 gap-0 [&>button]:hidden">
        <div className="flex items-center justify-between border-b px-5 py-4">
          <div className="flex items-center gap-2">
            {onBack && (
              <button
                aria-label="Back"
                onClick={onBack}
                className="rounded-sm p-1 text-muted-foreground hover:text-foreground hover:bg-transparent focus:outline-none"
              >
                <ChevronLeft className="h-5 w-5 opacity-70 transition-opacity hover:opacity-100" />
              </button>
            )}
            <DialogTitle className="text-base font-semibold">{title}</DialogTitle>
          </div>
          <button
            aria-label="Close"
            onClick={onClose}
            className="rounded-sm p-1 text-muted-foreground hover:text-foreground hover:bg-transparent focus:outline-none"
          >
            <CircleX className="h-5 w-5 opacity-70 transition-opacity hover:opacity-100" />
          </button>
        </div>

        <div className="px-5 py-5" onPaste={handlePaste}>
          <div className="mb-1 flex items-center justify-between">
            <label className="text-xs font-medium text-muted-foreground">What went wrong?</label>
          </div>
          <textarea
            rows={7}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            className={[
              "w-full rounded-lg border bg-background p-3 text-sm outline-none",
              "focus:ring focus:ring-[hsl(var(--brand-violet-300))]",
              error ? "border-red-500 focus:ring-red-500" : "",
            ].join(" ")}
            placeholder="What did you try?

What's missing?

What isn't working in the workflow?

Is this workflow valid?

Link to the nodepack GitHub.

Link to the model."
          />

          <div className="mt-3">
            {enableAttachments && (
              <>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">
                  Attachments{" "}
                  <span className="opacity-70">
                    (optional, max 5 images, <span className="sm:hidden">tap to select</span>
                    <span className="hidden sm:inline">drag & drop or paste</span>)
                  </span>
                </label>
                <label
                  className={[
                    "flex cursor-pointer items-center justify-between rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground transition-colors",
                    isDragging
                      ? "border-[hsl(var(--brand-violet-300))] bg-[hsl(var(--brand-violet-300))]/10"
                      : "border-muted-foreground/40 hover:border-muted-foreground/60 hover:bg-muted/30",
                  ].join(" ")}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                  <div className="flex items-center gap-2">
                    <ImagePlus className="h-4 w-4" />
                    <span className="font-medium">
                      {isDragging ? (
                        "Drop images here"
                      ) : files.length > 0 ? (
                        `${files.length} image${files.length > 1 ? "s" : ""} selected`
                      ) : (
                        <>
                          <span className="sm:hidden">Add images (tap to select)</span>
                          <span className="hidden sm:inline">
                            Add images (click, drag, or paste)
                          </span>
                        </>
                      )}
                    </span>
                  </div>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    multiple
                    onChange={handlePickFiles}
                    className="hidden"
                  />
                </label>
              </>
            )}

            {!enableAttachments && (
              <div className="flex items-center justify-between rounded-md border border-dashed border-muted-foreground/40 px-3 py-2 text-xs text-muted-foreground">
                <div>
                  <span className="font-medium">Attachments coming soon.</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground/70">
                  <ImagePlus className="h-4 w-4" />
                  <span>Uploads are disabled.</span>
                </div>
              </div>
            )}

            {enableAttachments && previews.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-3">
                {previews.map((src, index) => (
                  <div key={src} className="relative flex flex-col">
                    <div
                      className="relative h-48 w-64 overflow-hidden rounded-md border cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => setLightboxIndex(index)}
                    >
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={src}
                        alt={`Attachment ${index + 1}`}
                        className="h-full w-full object-cover"
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(index);
                        }}
                        className="absolute right-2 top-2 rounded-full bg-black/70 p-1.5 text-white hover:bg-black/90 transition-colors"
                        aria-label={`Remove attachment ${index + 1}`}
                      >
                        <CircleX className="h-5 w-5" />
                      </button>
                    </div>
                    <span className="mt-1 text-xs text-muted-foreground truncate w-64 px-1">
                      {files[index]?.name ?? `Image ${index + 1}`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && (
            <p className="mt-2 text-xs text-red-500" role="alert">
              {error}
            </p>
          )}

          <div className="mt-5 flex justify-end gap-2">
            <button
              onClick={onClose}
              className="rounded-md border px-3 py-1.5 text-sm hover:bg-muted"
            >
              Cancel
            </button>
            <button
              onClick={submitBug}
              disabled={!isValid || submitting}
              className="inline-flex items-center gap-2 rounded-md bg-foreground px-3 py-1.5 text-sm text-background disabled:opacity-50"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {submitting ? "Sending…" : "Send report"}
            </button>
          </div>
        </div>
      </DialogContent>

      {/* Lightbox for image preview */}
      {lightboxIndex !== null && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 backdrop-blur-sm"
          onClick={(e) => {
            e.stopPropagation();
            setLightboxIndex(null);
          }}
        >
          <div className="relative max-h-[90vh] max-w-[90vw]">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={previews[lightboxIndex]}
              alt={files[lightboxIndex]?.name ?? `Image ${lightboxIndex + 1}`}
              className="max-h-[90vh] max-w-[90vw] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex(null);
              }}
              className="absolute right-4 top-4 rounded-full bg-black/70 p-2 text-white hover:bg-black/90 transition-colors"
              aria-label="Close preview"
            >
              <CircleX className="h-6 w-6" />
            </button>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-lg bg-black/70 px-4 py-2 text-sm text-white">
              {files[lightboxIndex]?.name ?? `Image ${lightboxIndex + 1}`} ({lightboxIndex + 1}/
              {files.length})
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
}

export default function BugReport({ label = "Report a bug", className, source }: BugReportProps) {
  const [open, setOpen] = useState(false);

  const openModal = () => setOpen(true);
  const closeModal = () => setOpen(false);

  return (
    <>
      <button
        onClick={openModal}
        className={[
          "relative inline-flex items-center gap-2 rounded-md border",
          "border-[hsl(var(--brand-violet-300))]/50 bg-muted px-3 py-1.5 text-sm font-medium text-foreground",
          "shadow transition-all hover:bg-accent focus:outline-none focus:ring focus:ring-muted-foreground/50",
          className || "",
        ].join(" ")}
      >
        <Bug size={18} />
        {label}
      </button>

      <BugReportDialog open={open} onClose={closeModal} source={source} enableAttachments={true} />
    </>
  );
}
